import { useMemo } from 'react';
import Box from '@mui/material/Box';
import useGetMedias from '@/hooks/useGetMedias';
import ShowNoteForms from '@/components/common/notes/show-note-forms/show-note-forms';
import { fixTypeNoteConfigurationVersion2AsVersion1 } from '@/utils/common';
import CommentIcon from '@mui/icons-material/Comment';
import ShowNoteContent from '@/components/common/notes/show-note-content/show-note-content';
import ShowNoteImages from '@/components/common/notes/show-note-images/show-note-images';
import ShowNoteAudios from '@/components/common/notes/show-note-audios/show-note-audios';
import ShowNoteDocuments from '@/components/common/notes/show-note-documents/show-note-documents';
import ShowNoteSigns from '@/components/common/notes/show-note-signs/show-note-signs';
import ShowNoteExternalIdentification from '@/components/common/notes/show-note-external-identification/show-note-external-identification';
import NoteTitle from '@/components/common/notes/note-title/note-title';

interface Props {
    note: {
        iconId?: string;
        name: string;
        createdAt: string;
        data: {
            auth: {
                firstName: string;
                lastName: string;
                username: string;
            };
            customFields: any;
            medias: {
                customFields: any;
                id: string;
            }[];
            signatures: any[];
        };
        customFields: any;
        forms: any;
        coords: any;
        configuration: {
            customFields: any;
            isForwarded: boolean;
        };
        typeNote: any;
    };
}

const GlobalNoteStandard = ({ note }: Props) => {
    const time = useMemo(() => note?.createdAt, [note]);

    const authInfo = useMemo(() => note?.data?.auth, [note]);

    const content = useMemo(() => note?.data?.customFields?.forms?._note_?.content, [note]);

    const mediasIds = useMemo(() => note?.data?.medias.map((media) => media.id), [note]);
    const { images, audios, documents, isLoading: isLoadingMedias } = useGetMedias({ mediasIds });

    const signs = useMemo(() => note?.data?.signatures, [note]);

    const externalIdentification = useMemo(() => note?.data?.customFields?.external_identification, [note]);

    const formsData = useMemo(
        () => fixTypeNoteConfigurationVersion2AsVersion1(note?.typeNote, note?.data?.customFields?.forms),
        [note],
    );
    const formsOrigin = useMemo(() => note?.typeNote?.configuration?.customFields?.componentForms, [note]);

    const title = useMemo(() => formsData?._note_?.title ?? '---', [formsData]);

    return (
        <Box
            sx={{
                display: 'grid',
                gap: 1,
                backgroundColor: 'white',
                py: 1,
            }}
        >
            <NoteTitle icon={<CommentIcon />} title={title} time={time} authInfo={authInfo} />

            {content && <ShowNoteContent content={content} />}

            {!isLoadingMedias && images && images.length > 0 && (
                <ShowNoteImages imagesUrls={images.map((item) => item.urlToDownload)} />
            )}

            {!isLoadingMedias && audios && audios.length > 0 && (
                <ShowNoteAudios audios={audios.map((item) => item.urlToDownload)} />
            )}

            {!isLoadingMedias && documents && documents.length > 0 && (
                <ShowNoteDocuments
                    documents={documents.map((item) => ({
                        urlToDownload: item.urlToDownload,
                        name: item.name,
                    }))}
                />
            )}

            {signs && signs.length > 0 && <ShowNoteSigns signs={signs} />}

            {externalIdentification && (
                <ShowNoteExternalIdentification externalIdentification={externalIdentification} />
            )}

            {formsData && formsOrigin && formsOrigin.length > 0 && (
                <ShowNoteForms formsData={formsData} formsOrigin={formsOrigin} typeNote={note?.typeNote} />
            )}
        </Box>
    );
};

export default GlobalNoteStandard;
